import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { AnnouncementSettings, SellerListing } from '@/types/marketplace'
import type { SellerVerification } from '@/lib/marketplace/seller-verification'
import type { SellerStat } from '@/components/marketplace/seller/seller-stats'
import type { HeaderState } from '@/lib/community/owner-hub'

import Link from 'next/link'
import { useEffect } from 'react'

import {
    CalendarDays,
    CheckCircle2,
    Clock,
    Handshake,
    Package,
    Zap
} from 'lucide-react'

import { getPublicSellerByUsername } from '@/lib/marketplace/sellers'
import { getSellerListings } from '@/lib/marketplace/listings'
import { getAnnouncement, getLegal } from '@/lib/db/settings'
import { getHeaderState } from '@/lib/community/owner-hub'
import { getBaseUrl } from '@/lib/marketplace/url'
import { formatMonthYear, telegramLink } from '@/lib/marketplace/format'
import {
    buildSellerVerifications,
    getSellerVerifiedState,
    responseTimeLabel
} from '@/lib/marketplace/seller-verification'
import { trackEvent } from '@/lib/analytics/client'
import { canonicalOrigin } from '@/lib/seo/canonical'
import { metaDescription } from '@/lib/seo/metadata'
import { WEBSITE_ID, jsonLdGraph } from '@/lib/seo/jsonld'

import { Seo } from '@/components/generals/seo'
import { SiteShell, Section } from '@/components/marketplace/site-shell'
import { EmptyState } from '@/components/marketplace/empty-state'
import { SellerProfileHeader } from '@/components/marketplace/seller/seller-profile-header'
import { SellerListingCard } from '@/components/marketplace/seller/seller-listing-card'
import { SellerVerificationPanel } from '@/components/marketplace/seller/seller-verification-panel'
import { SellerTrustBar } from '@/components/marketplace/seller/seller-trust-bar'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

/**
 * Exactly what the PUBLIC profile is allowed to know about a seller.
 *
 * Built on the server from the full record, so the phone number, the email,
 * the Telegram numeric id, the moderation notes and every verification
 * document stay behind the query and never reach the browser. Only the
 * OUTCOME of a check is serialised, in `verifications`.
 */
type PublicSeller = {
    id: string
    name: string
    /**
     * The handle SHOWN and used in this profile's address: the seller's own
     * platform handle when they have chosen one, otherwise their Telegram
     * username. Display only.
     */
    handle: string | null
    /**
     * The Telegram username, carried separately because it is what the CONTACT
     * link is built from. Collapsing the two is how a seller with a platform
     * handle would end up with a Telegram link into an account that is not
     * theirs. The numeric Telegram id stays server-side, as ever.
     */
    telegramUsername: string | null
    avatarUrl: string | null
    bio: string | null
    /** Self-declared profile details. Never a verification. */
    country: string | null
    websiteUrl: string | null
    instagramHandle: string | null
    isBusiness: boolean
    restricted: boolean
}

interface SellerPageProps {
    seller: PublicSeller
    listings: Array<SellerListing>
    disclosure: string
    announcement: AnnouncementSettings
    profileUrl: string
    baseUrl: string
    memberSince: string
    completedDeals: number
    /** Null whenever nothing measured them. The stat is then not rendered at all. */
    responseRate: number | null
    responseTime: string | null
    verifications: Array<SellerVerification>
    /** The single server-side verification answer, plus its hover text. */
    verified: boolean
    verifiedTooltip: string
    header: HeaderState
}

const SellerPage: FC<SellerPageProps> = ({
    seller,
    listings,
    disclosure,
    announcement,
    profileUrl,
    baseUrl,
    memberSince,
    completedDeals,
    responseRate,
    responseTime,
    verifications,
    verified,
    verifiedTooltip,
    header
}): ReactNode => {
    const active = listings.filter((listing) => listing.status === 'active')
    const sold = listings.filter((listing) => listing.status === 'sold')
    const contactHref = telegramLink(seller.telegramUsername)

    const profileLinks = [
        contactHref,
        seller.websiteUrl,
        seller.instagramHandle ? `https://instagram.com/${seller.instagramHandle}` : null
    ].filter((link): link is string => Boolean(link))

    /**
     * Icons are components, so the strip is assembled here rather than in
     * `getServerSideProps` where props must stay serialisable. Every value
     * still comes from the server. A stat with no data behind it is dropped.
     */
    const stats: Array<SellerStat> = [
        { label: 'Member since', value: memberSince, icon: CalendarDays },
        { label: 'Active listings', value: String(active.length), icon: Package },
        { label: 'Completed deals', value: String(completedDeals), icon: Handshake },
        ...(responseRate === null ? [] : [{ label: 'Response rate', value: `${responseRate}%`, icon: Zap }]),
        ...(responseTime === null ? [] : [{ label: 'Response time', value: responseTime, icon: Clock }])
    ]

    useEffect(() => {
        trackEvent({ type: 'seller_profile_view', sellerId: seller.id })
    }, [seller.id])

    return (
        <SiteShell disclosure={disclosure} announcement={announcement} {...header}>
            <Seo
                title={seller.name}
                description={metaDescription(
                    `${seller.name} on Bazio. ${active.length} active listings, member since ${memberSince}.`
                )}
                canonical={profileUrl || undefined}
                baseUrl={baseUrl}
                /*
                 * The seller's own photo is the honest preview here, and it is
                 * square rather than 1200x630, so its dimensions are left
                 * unstated and the crawler measures it. With no photo the
                 * component falls back to the Bazio lockup by itself.
                 */
                ogImage={seller.avatarUrl ?? undefined}
                ogImageAlt={seller.name}
                jsonLd={jsonLdGraph([
                    {
                        '@type': 'ProfilePage',
                        url: profileUrl || undefined,
                        /* Joins this page to the one site entity rather than
                           standing alone as an unrelated document. */
                        isPartOf: { '@id': `${canonicalOrigin(baseUrl)}/${WEBSITE_ID}` },
                        mainEntity: {
                            '@type': 'Person',
                            name: seller.name,
                            alternateName: seller.handle ? `@${seller.handle}` : undefined,
                            image: seller.avatarUrl ?? undefined,
                            url: profileUrl || undefined,
                            /* Only links the seller actually published on this
                               page, so the entity graph agrees with what a
                               visitor can see. */
                            sameAs: profileLinks.length > 0 ? profileLinks : undefined
                        }
                    }
                    /*
                     * NO BreadcrumbList here, deliberately. This page renders no
                     * visible trail, and markup describing a hierarchy the
                     * visitor cannot see is the one kind of structured data that
                     * actively costs you. `/category/[slug]` and the listing page
                     * both draw a real breadcrumb, so both mark one up.
                     */
                ])}
            />

            {/* ---------------- Profile header ---------------- */}
            <Section id='seller-profile' className='py-8 sm:py-10'>
                <SellerProfileHeader
                    name={seller.name}
                    username={seller.handle}
                    avatarUrl={seller.avatarUrl}
                    bio={seller.bio}
                    country={seller.country}
                    websiteUrl={seller.websiteUrl}
                    instagramHandle={seller.instagramHandle}
                    isBusiness={seller.isBusiness}
                    restricted={seller.restricted}
                    verifications={verifications}
                    verified={verified}
                    verifiedTooltip={verifiedTooltip}
                    stats={stats}
                    telegramHref={contactHref}
                    profileUrl={profileUrl}
                    onTelegramClick={() => trackEvent({ type: 'telegram_contact_click', sellerId: seller.id })}
                />
            </Section>

            {/* ---------------- Listings + verification ---------------- */}
            <Section id='seller-listings' className='pb-12 sm:pb-14'>
                <div className='grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10'>
                    <div className='min-w-0'>
                        <h2 className='type-display text-2xl text-ink-900 sm:text-3xl'>
                            {`Active listings (${active.length})`}
                        </h2>

                        <span aria-hidden='true' className='mt-2.5 block h-[3px] w-20 bg-brand-red' />

                        {active.length === 0 ? (
                            <EmptyState
                                className='mt-7'
                                icon={Package}
                                title='No active listings'
                                description='This seller has nothing live on the marketplace right now.'
                                action={(
                                    <Link
                                        href='/browse'
                                        className={cn(buttonVariants({ variant: 'outline' }), 'type-label mta-border mta-focus h-11 rounded-mta-sm bg-paper px-5 text-[11px] text-ink-900 hover:bg-ink-900 hover:text-paper')}
                                    >
                                        Browse the marketplace
                                    </Link>
                                )}
                            />
                        ) : (
                            <ul className='mt-7 space-y-5'>
                                {active.map((listing, index) => (
                                    <li key={listing.id}>
                                        <SellerListingCard listing={listing} priority={index === 0} />
                                    </li>
                                ))}
                            </ul>
                        )}

                        {sold.length > 0 && (
                            <>
                                <h2 className='type-display mt-14 text-2xl text-ink-900 sm:text-3xl'>
                                    {`Sold (${sold.length})`}
                                </h2>

                                <span aria-hidden='true' className='mt-2.5 block h-[3px] w-20 bg-brand-red' />

                                <p className='mt-4 text-sm leading-relaxed text-text-muted'>
                                    Past listings this seller marked as sold.
                                </p>

                                <ul className='mt-6 space-y-5'>
                                    {sold.map((listing) => (
                                        <li key={listing.id}>
                                            <SellerListingCard listing={listing} />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>

                    <aside className='min-w-0'>
                        <SellerVerificationPanel verifications={verifications} disclosure={disclosure} />

                        <div className='mta-border mt-5 rounded-mta-lg bg-surface-muted p-5'>
                            <p className='flex items-start gap-2 text-xs leading-relaxed text-text-muted'>
                                <CheckCircle2 className='mt-px size-4 shrink-0 text-ink-900/45' aria-hidden='true' />
                                A badge reflects only the specific check Bazio performed. It is never a guarantee that a deal is safe.
                            </p>

                            <Link
                                href='/report'
                                className='type-label mta-focus mta-scroll-safe mt-4 inline-block rounded-mta-xs text-[11px] text-brand-red-ink underline-offset-4 hover:underline'
                            >
                                Report this seller
                            </Link>
                        </div>
                    </aside>
                </div>
            </Section>

            {/* ---------------- Trust strip ---------------- */}
            <Section className='pb-16'>
                <SellerTrustBar />
            </Section>
        </SiteShell>
    )
}

export const getServerSideProps: GetServerSideProps<SellerPageProps> = async ({ req, params }) => {
    const username = typeof params?.username === 'string' ? params.username : ''
    const seller = await getPublicSellerByUsername(username)

    if (!seller) return { notFound: true }

    const [listings, legal, announcement, header] = await Promise.all([
        getSellerListings(seller.id, 48),
        getLegal(),
        getAnnouncement(),
        getHeaderState(req)
    ])

    const baseUrl = getBaseUrl(req)
    const profileAddress = seller.profileHandle ?? seller.telegramUsername
    const verifications = buildSellerVerifications(seller)
    const verifiedState = getSellerVerifiedState(seller, verifications)

    return {
        props: {
            seller: {
                id: seller.id,
                /*
                    The seller's OWN display name, which the profile form owns.
                    A business name still wins, because that is the trading name
                    a buyer is dealing with.
                */
                name: seller.businessName || seller.displayName,
                handle: seller.profileHandle ?? seller.telegramUsername,
                telegramUsername: seller.telegramUsername,
                /* Their uploaded photo when they set one, Telegram's otherwise;
                   `avatar_url` already holds whichever is current. */
                avatarUrl: seller.businessLogoUrl || seller.avatarUrl,
                bio: seller.bio,
                country: seller.country,
                websiteUrl: seller.websiteUrl || seller.businessWebsite,
                instagramHandle: seller.instagramHandle,
                isBusiness: seller.isBusiness,
                restricted: seller.sellerStatus === 'restricted'
            },
            listings,
            disclosure: legal.notAPartyStatement,
            announcement,
            /* Canonical on the handle they chose, so the shared link is the one
               that survives a Telegram username changing hands. */
            profileUrl: profileAddress && baseUrl ? `${baseUrl}/seller/${profileAddress}` : '',
            baseUrl,
            memberSince: formatMonthYear(seller.createdAt),
            completedDeals: seller.completedDeals,
            responseRate: seller.responseRate,
            responseTime: responseTimeLabel(seller.averageResponseMinutes),
            verifications,
            verified: verifiedState.verified,
            verifiedTooltip: verifiedState.tooltip,
            header
        }
    }
}

export default SellerPage
